import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Router } from "@angular/router";

import swal from "sweetalert2";
import {
  CatAreaIA,
  ImportacionPlan,
  Matriz,
  MonitoreoMatriz,
  RiesgoProceso,
} from "../../shared/model/iso42001/matriz-riesgo.model";
import { ProximoMonitoreo, pideAviso, proximoMonitoreo } from "../monitoreo";
import { MatrizRiesgoService } from "../matriz-riesgo.service";
import { AuthService } from "../../security/auth.service";
import { tieneAccesoMenu } from "../../shared/menu/acceso-menu";

/**
 * Un color por área (29/09/2026, Norman: «que cada una tenga diferente
 * color, dependiendo el área»): la banda izquierda de la tarjeta y su clave.
 * Sale del id del área, así que un área tiene siempre el mismo color, y con
 * doce no se repite ninguno mientras haya doce áreas o menos (hoy son diez).
 * Tonos oscuros a propósito: el blanco de la clave se lee sobre todos
 * (4.9:1 o más). En QA: 1 Dirección General azul … 9 Desarrollo índigo,
 * 10 Pruebas mostaza.
 */
const COLORES_AREA = [
  "#1d4ed8", // azul
  "#047857", // verde
  "#7c3aed", // violeta
  "#c2410c", // naranja
  "#0e7490", // cian
  "#be185d", // rosa
  "#4d7c0f", // olivo
  "#a21caf", // fucsia
  "#4338ca", // índigo
  "#a16207", // mostaza
  "#0f766e", // turquesa
  "#92400e", // café
];
const COLOR_SIN_AREA = "#475569";

/** Un riesgo o un activo al que ya le toca (o le está por tocar) monitoreo. */
interface AvisoMonitoreo {
  item: MonitoreoMatriz;
  matriz: Matriz;
  proximo: ProximoMonitoreo;
}

/**
 * Gestión de Riesgos: el tablero de las matrices.
 *
 * Desde aquí se crea la matriz de cada área. Todas usan el MISMO formato
 * FOS-005 y los MISMOS criterios (severidad, impacto, estrategias): eso es la
 * metodología y se comparte a propósito, para poder comparar áreas.
 *
 * Lo único que cambia por matriz es de quién son sus renglones, la clave de
 * sus folios y si trabaja con ACTIVOS —que hoy sólo aplica a TI/IA—. Las
 * demás áreas llevan su General y su Plan de Gestión.
 *
 * Al crear una, el backend le da de alta también su renglón en el menú
 * lateral; aquí se refresca el menú para verlo sin cerrar sesión.
 */
@Component({
  selector: "app-gestion-matrices-ia",
  standalone: true,
  templateUrl: "./gestion-matrices-ia.component.html",
  styleUrls: ["./gestion-matrices-ia.component.css"],
  imports: [CommonModule, FormsModule, MatTooltipModule],
})
export class GestionMatricesIAComponent implements OnInit {
  matrices: Matriz[] = [];
  areas: CatAreaIA[] = [];
  cargando = true;
  error = "";

  /**
   * Alta en curso; null si no se está capturando. Solo alta: una matriz que
   * ya existe se edita desde adentro (EditarMatrizIAComponent, botón Editar
   * del encabezado de la matriz).
   */
  borrador: Matriz | null = null;
  guardando = false;

  /** La matriz sembrada con el módulo: no se da de baja ni se elimina. */
  private static readonly MATRIZ_IA = 1;

  constructor(
    private service: MatrizRiesgoService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
  ) {}

  /**
   * Administrar el tablero (crear, dar de baja, eliminar) es de quien tiene
   * el menú «Gestión de Riesgos»: la misma regla con la que el servidor lo
   * acepta (MatrizBuss.exigePermiso) y la del botón Editar del libro. La
   * ruta solo pide sesión, así que sin esto quien llegara por la URL veía
   * botones que el servidor le iba a rechazar.
   */
  get puedeAdministrar(): boolean {
    return tieneAccesoMenu(this.authService.getMenuList(), "gestionRiesgos");
  }

  /** La matriz cuyo estatus se está cambiando: su interruptor espera. */
  cambiandoEstatus: number | null = null;

  /** Al recargar, cada tarjeta sigue siendo la misma (y su cambio se ve). */
  porId(_: number, m: Matriz): number | undefined {
    return m.idMatriz;
  }

  ngOnInit(): void {
    this.cargar();
  }

  /**
   * Con silencioso no se esconde el tablero mientras llega: se usa después
   * de dar de baja o reactivar, para que la tarjeta no parpadee.
   */
  private cargar(silencioso = false): void {
    if (!silencioso) {
      this.cargando = true;
    }
    this.service.consultarMatrices().subscribe({
      next: (res) => {
        this.cargando = false;
        if (!res || res.codigoError) {
          this.error = (res && res.mensajeError) || "No se pudieron consultar las matrices.";
        } else {
          this.matrices = res.matrices || [];
          this.areas = res.areas || [];
          this.cargarMonitoreos();
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargando = false;
        this.error = "No se pudieron consultar las matrices.";
        this.cdr.detectChanges();
      },
    });
  }

  /* ---------- Alta ---------- */

  /* ---------- Carga del formato FOS-005 ----------
   * Dos pasos a propósito: primero se lee el archivo y se muestra qué se
   * encontró, sin escribir nada; sólo al dar "Crear" se guarda. Así el
   * usuario puede cancelar sin dejar rastro, que es justo lo que pidió. */

  /**
   * «¿Ya tienes el formato FOS-005 lleno?» (29/09/2026): encendido se sube
   * el archivo y la matriz nace con su contenido; apagado no se sube nada y
   * se crea vacía. Arranca apagado.
   */
  conFormato = false;

  alternarFormato(): void {
    if (this.leyendoArchivo || this.guardando) {
      return;
    }
    this.conFormato = !this.conFormato;
    // Apagarlo suelta el archivo que se hubiera leído: así no se crea con
    // él por descuido.
    if (!this.conFormato) {
      this.quitarFormato();
    }
  }

  /**
   * «¿Trabaja con activos?» (29/09/2026): un interruptor, como el del
   * formato. Encendido es SI: pestaña de Activos y expediente por activo.
   */
  alternarActivos(): void {
    if (!this.borrador || this.leyendoArchivo || this.guardando) {
      return;
    }
    this.borrador.aplicaActivos = this.borrador.aplicaActivos === "SI" ? "NO" : "SI";
    // La vista previa depende de esto (la hoja «Activos» se omite o se
    // exige): con un formato ya leído, se vuelve a leer con el valor nuevo.
    if (this.conFormato && this.archivoFormato) {
      this.leerFormato(this.archivoFormato);
    }
  }

  /** El .xlsx elegido. Se conserva porque al confirmar se manda otra vez. */
  archivoFormato: File | null = null;
  nombreArchivo = "";
  /** Lo que se leyó del archivo. null = todavía no se ha subido ninguno. */
  plan: ImportacionPlan | null = null;
  leyendoArchivo = false;

  elegirFormato(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const archivo = input.files && input.files[0];
    // Se limpia siempre para poder volver a elegir el MISMO archivo después
    // de corregirlo: sin esto, el navegador no dispara el evento otra vez.
    input.value = "";
    if (!archivo || !this.borrador) {
      return;
    }
    this.leerFormato(archivo);
  }

  /**
   * Lee el archivo en el servidor, sin escribir nada, y deja su plan. También
   * se llama al cambiar «¿Trabaja con activos?», porque el plan depende de eso.
   */
  private leerFormato(archivo: File): void {
    if (!this.borrador) {
      return;
    }
    this.archivoFormato = archivo;
    this.nombreArchivo = archivo.name;
    this.plan = null;
    this.error = "";
    this.leyendoArchivo = true;
    this.cdr.detectChanges();

    this.service.previaImportacion(archivo, this.borrador).subscribe({
      next: (res) => {
        this.leyendoArchivo = false;
        if (!res || res.codigoError) {
          this.error = (res && res.mensajeError)
            || "No se pudo leer el archivo. Revisa el log del servidor.";
          this.archivoFormato = null;
          this.nombreArchivo = "";
        } else {
          this.plan = res.plan || null;
        }
        this.cdr.detectChanges();
      },
      error: (e) => {
        this.leyendoArchivo = false;
        this.archivoFormato = null;
        this.nombreArchivo = "";
        this.error = e && e.status === 404
          ? "El servidor no tiene todavía la carga del formato. Falta desplegar el backend."
          : "No se pudo leer el archivo (HTTP " + (e && e.status !== undefined ? e.status : "?") + ").";
        this.cdr.detectChanges();
      },
    });
  }

  quitarFormato(): void {
    this.archivoFormato = null;
    this.nombreArchivo = "";
    this.plan = null;
    this.error = "";
  }

  /** El folio que le va a tocar a cada riesgo, con la clave capturada. */
  folioPropuesto(i: number): string {
    const clave = ((this.borrador && this.borrador.clave) || "XX").toUpperCase();
    return "RSK-" + clave + "-" + String(i + 1).padStart(3, "0");
  }

  /**
   * Cortes que la carga deja pendientes: traían fecha y no resultado
   * (29/09/2026). Llevan la marca de la migración de la matriz de IA y se
   * completan en el expediente del riesgo, con «Capturar resultado».
   */
  pendientes(r: RiesgoProceso): number {
    return (r.seguimientos || []).filter(
      (s) => !!s.resultado && s.resultado.indexOf("NA - el resultado de este corte") === 0,
    ).length;
  }

  get totalPendientes(): number {
    return ((this.plan && this.plan.riesgos) || []).reduce((n, r) => n + this.pendientes(r), 0);
  }

  /** Severidad en vivo, como la calcula el backend: probabilidad x impacto. */
  severidad(r: RiesgoProceso): number | null {
    return r.probInicial && r.impactoInicial ? r.probInicial * r.impactoInicial : null;
  }

  claseSeveridad(n: number | null): string {
    if (!n) { return ""; }
    return n <= 4 ? "baja" : n <= 8 ? "media" : n <= 12 ? "alta" : "critico";
  }

  nueva(): void {
    this.error = "";
    this.conFormato = false;
    this.quitarFormato();
    this.borrador = {
      nombre: "",
      clave: "",
      idCatArea: null,
      alcance: "",
      proceso: "PRO-001 Administración de Riesgos",
      aplicaActivos: "NO",
    };
  }

  cancelar(): void {
    this.borrador = null;
    this.error = "";
    this.conFormato = false;
    this.quitarFormato();
  }

  guardar(): void {
    if (!this.borrador || this.guardando) {
      return;
    }
    const b = this.borrador;
    if (!b.nombre || !b.nombre.trim()) {
      this.error = "El nombre de la matriz es obligatorio.";
      return;
    }
    if (!/^[A-Za-z]{2,6}$/.test((b.clave || "").trim())) {
      this.error = "La clave del folio debe ser de 2 a 6 letras, sin espacios ni acentos (ej. RH, CONTA).";
      return;
    }
    // Encendido, el formato es obligatorio: si no, que lo apague y se crea vacía.
    if (this.conFormato && !(this.archivoFormato && this.plan && this.plan.valido)) {
      this.error = this.plan && !this.plan.valido
        ? "El formato tiene errores: corrígelo y vuelve a subirlo, o apaga el interruptor para crear la matriz vacía."
        : "Sube el formato FOS-005, o apaga el interruptor para crear la matriz vacía.";
      return;
    }
    this.error = "";
    this.guardando = true;

    const alTerminar = (res: any) => {
      this.guardando = false;
      if (!res || res.codigoError) {
        this.error = (res && res.mensajeError) || "No se pudo guardar la matriz.";
        this.cdr.detectChanges();
        return;
      }
      this.borrador = null;
      // Ya no se refresca el menú: las matrices no cuelgan del panel
      // lateral, se abren desde este tablero con un clic en su tarjeta.
      this.avisarAlta(res.idGenerado);
    };

    // Con un formato leido y sin errores, se crea la matriz CON su contenido
    // en una sola llamada; el backend lo hace todo en una transaccion.
    if (this.conFormato && this.archivoFormato && this.plan && this.plan.valido) {
      this.service.importarMatriz(this.archivoFormato, b, this.plan.huella).subscribe({
        next: (res) => alTerminar(res),
        error: (e) => {
          this.guardando = false;
          this.error = e && e.status === 404
            ? "El servidor no tiene todavía la carga del formato. Falta desplegar el backend."
            : "No se pudo crear la matriz (HTTP "
              + (e && e.status !== undefined ? e.status : "?") + "). No se guardó nada.";
          this.cdr.detectChanges();
        },
      });
      return;
    }

    this.service.crearMatriz(b).subscribe({
      next: (res) => alTerminar(res),
      error: () => alTerminar(null),
    });
  }

  private avisarAlta(idGenerado?: number): void {
    this.cargar();
    swal.fire({
      title: "Matriz creada",
      text: "Ya aparece en este tablero. ¿Quieres abrirla ahora?",
      icon: "success",
      showCancelButton: true,
      confirmButtonText: "Abrir la matriz",
      cancelButtonText: "Quedarme aquí",
    }).then((r) => {
      // Sin recargar la página: no hay menú nuevo que traer. Se navega por el
      // router, que es más rápido y no pierde el estado del tablero.
      if (r.isConfirmed && idGenerado) {
        this.abrir({ idMatriz: idGenerado });
      }
    });
  }

  /* ---------- Acciones sobre una matriz ---------- */

  /** Abre la matriz. Una dada de baja no se abre hasta reactivarla. */
  abrir(m: Matriz): void {
    if (!m.idMatriz || (m.estatus && !this.activa(m))) {
      return;
    }
    this.router.navigate(["/RIESGOS/matriz", m.idMatriz]);
  }

  esFija(m: Matriz): boolean {
    return m.idMatriz === GestionMatricesIAComponent.MATRIZ_IA;
  }

  activa(m: Matriz): boolean {
    return m.estatus === "ACTIVO";
  }

  alternarEstatus(m: Matriz): void {
    if (!m.idMatriz) {
      return;
    }
    const nuevo = this.activa(m) ? "INACTIVO" : "ACTIVO";
    const texto = nuevo === "INACTIVO"
      ? "Ya no se podrá abrir hasta reactivarla. Los riesgos capturados se conservan."
      : "Se vuelve a poder abrir desde este tablero.";
    swal.fire({
      title: nuevo === "INACTIVO" ? "¿Dar de baja la matriz?" : "¿Reactivar la matriz?",
      text: texto,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, continuar",
      cancelButtonText: "Cancelar",
    }).then((r) => {
      if (!r.isConfirmed) {
        return;
      }
      // El cambio se ve en el momento (29/09/2026, «que se vea el efecto»):
      // la perilla se corre y la tarjeta cambia de color. Si el servidor lo
      // rechaza, regresa como estaba.
      const antes = m.estatus;
      m.estatus = nuevo;
      this.cambiandoEstatus = m.idMatriz!;
      this.cdr.detectChanges();
      const regresa = (motivo: string) => {
        m.estatus = antes;
        this.cambiandoEstatus = null;
        this.cdr.detectChanges();
        swal.fire("No se pudo", motivo, "error");
      };
      this.service.cambiarEstatusMatriz(m.idMatriz!, nuevo).subscribe({
        next: (res) => {
          // Sin cuerpo tambien es fallo: el manejador global responde 200
          // vacio y darlo por bueno recargaba como si hubiera funcionado.
          if (!res || res.codigoError) {
            regresa((res && res.mensajeError) || "");
            return;
          }
          this.cambiandoEstatus = null;
          swal.fire({
            toast: true,
            position: "top-end",
            icon: "success",
            title: nuevo === "INACTIVO" ? "Matriz dada de baja" : "Matriz reactivada",
            showConfirmButton: false,
            timer: 2500,
            timerProgressBar: true,
          });
          // Ya no se recarga la página: las matrices no están en el menú
          // lateral, así que basta con volver a pedir el listado, sin
          // esconder el tablero.
          this.cargar(true);
        },
        error: () => regresa("Error al cambiar el estatus."),
      });
    });
  }

  /**
   * Segunda oportunidad cuando la matriz tiene contenido: se puede borrar
   * completa, pero pidiendo la contraseña del propio usuario. El backend
   * valida además que su perfil lo permita; aquí sólo se recoge la clave.
   *
   * Se dice EXACTAMENTE qué se va a llevar por delante: es lo único que no
   * se puede deshacer en todo el módulo.
   */
  private ofrecerBorradoCompleto(m: Matriz, motivo: string): void {
    swal.fire({
      title: "Esta matriz tiene contenido",
      html:
        "<p style='margin:0 0 10px'>" + motivo + "</p>"
        + "<p style='margin:0 0 10px'>Se puede borrar <b>completa</b>: sus riesgos, sus cortes de "
        + "monitoreo, sus versiones del documento, sus activos, sus evidencias y su bitácora. "
        + "<b>No se puede deshacer.</b></p>"
        + "<p style='margin:0'>Escribe tu contraseña para confirmar:</p>",
      icon: "warning",
      input: "password",
      inputPlaceholder: "Tu contraseña",
      inputAttributes: { autocomplete: "current-password" },
      showCancelButton: true,
      confirmButtonText: "Borrar todo",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#b3261e",
    }).then((r) => {
      if (!r.isConfirmed || !r.value || !m.idMatriz) {
        return;
      }
      this.service.eliminarMatrizCompleta(m.idMatriz, r.value).subscribe({
        next: (res) => {
          if (!res || res.codigoError) {
            swal.fire("No se pudo borrar",
              (res && res.mensajeError) || "No se recibió respuesta del servidor.", "error");
            return;
          }
          swal.fire("Matriz borrada", "Se eliminó junto con todo su contenido.", "success");
          this.cargar();
        },
        error: (e) => swal.fire("No se pudo borrar",
          "Error al borrar la matriz (HTTP "
          + (e && e.status !== undefined ? e.status : "?") + ").", "error"),
      });
    });
  }

  eliminar(m: Matriz): void {
    if (!m.idMatriz) {
      return;
    }
    swal.fire({
      title: "¿Eliminar la matriz?",
      text: (m.nombre || "") + " — se borra y no se puede deshacer. "
        + "Sólo procede si no tiene riesgos ni activos capturados.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then((r) => {
      if (!r.isConfirmed) {
        return;
      }
      this.service.eliminarMatriz(m.idMatriz!).subscribe({
        next: (res) => {
          // Sin cuerpo tambien es fallo: el manejador global responde 200
          // vacio y darlo por bueno recargaba como si hubiera funcionado.
          if (!res || res.codigoError) {
            // Si el rechazo es porque tiene contenido, se ofrece el borrado
            // completo en vez de dejar al usuario sin salida. Cualquier otro
            // motivo (permiso, matriz base) se muestra tal cual.
            const motivo = (res && res.mensajeError) || "";
            if (motivo.indexOf("vacíela antes") >= 0) {
              this.ofrecerBorradoCompleto(m, motivo);
              return;
            }
            swal.fire("No se pudo eliminar", motivo, "error");
            return;
          }
          // Ya no se recarga la página: las matrices no están en el menú
          // lateral, así que basta con volver a pedir el listado.
          this.cargar();
        },
        error: () => swal.fire("No se pudo eliminar", "Error al eliminar la matriz.", "error"),
      });
    });
  }

  /* ---------- Presentación ---------- */

  nombreArea(m: Matriz): string {
    if (m.areaNombre) {
      return m.areaNombre;
    }
    const a = this.areas.find((x) => x.id === m.idCatArea);
    return (a && a.area) || "Sin área";
  }

  /* ---------- Avisos de monitoreo (29/09/2026, solo en pantalla) ----------
   * «Te toca monitorear»: lo que vence de las matrices en cuya lista está
   * quien entra (se elige en «Editar» de cada matriz). Y en cada tarjeta,
   * cuántos monitoreos tiene vencidos o por vencer. La cuenta es la de
   * monitoreo.ts, la misma que se ve dentro de la matriz. */

  /** Los de mis matrices, del más atrasado al más próximo. */
  misAvisos: AvisoMonitoreo[] = [];
  private conteos = new Map<number, { vencidos: number; porVencer: number }>();
  private monitoreos: MonitoreoMatriz[] = [];
  private misMatrices = new Set<number>();

  private cargarMonitoreos(): void {
    this.service.consultarMonitoreos().subscribe({
      next: (res) => {
        // Sin permiso o sin respuesta, simplemente no hay avisos.
        this.monitoreos = (res && !res.codigoError && res.monitoreos) || [];
        this.misMatrices = new Set((res && !res.codigoError && res.misMatrices) || []);
        this.recalcularAvisos();
        this.cdr.detectChanges();
      },
      error: () => undefined,
    });
  }

  private recalcularAvisos(): void {
    const porId = new Map(this.matrices.filter((m) => this.activa(m)).map((m) => [m.idMatriz, m] as [number, Matriz]));
    const avisos: AvisoMonitoreo[] = [];
    this.conteos = new Map();
    for (const item of this.monitoreos) {
      const matriz = porId.get(item.idMatriz);
      const proximo = proximoMonitoreo(item.periodicidad, item.ultimoCorte, item.fechaIdentificacion,
        item.tipo === "ACTIVO");
      if (!matriz || !pideAviso(proximo)) {
        continue;
      }
      const c = this.conteos.get(item.idMatriz) || { vencidos: 0, porVencer: 0 };
      if (proximo.estado === "VENCIDO") {
        c.vencidos++;
      } else {
        c.porVencer++;
      }
      this.conteos.set(item.idMatriz, c);
      if (this.misMatrices.has(item.idMatriz)) {
        avisos.push({ item, matriz, proximo });
      }
    }
    this.misAvisos = avisos.sort((a, b) => (a.proximo.dias ?? 0) - (b.proximo.dias ?? 0));
  }

  /** Cuántos vencidos y por vencer tiene la matriz; nada si está al día. */
  conteo(m: Matriz): { vencidos: number; porVencer: number } | undefined {
    return this.conteos.get(m.idMatriz!);
  }

  get resumenMisAvisos(): string {
    const vencidos = this.misAvisos.filter((a) => a.proximo.estado === "VENCIDO").length;
    const porVencer = this.misAvisos.length - vencidos;
    const partes: string[] = [];
    if (vencidos) {
      partes.push(vencidos + (vencidos === 1 ? " vencido" : " vencidos"));
    }
    if (porVencer) {
      partes.push(porVencer + " por vencer");
    }
    return partes.join(" y ") + " en las matrices donde recibes avisos.";
  }

  colorArea(m: Matriz): string {
    const id = m.idCatArea;
    if (id == null || id <= 0) {
      return COLOR_SIN_AREA;
    }
    return COLORES_AREA[(id - 1) % COLORES_AREA.length];
  }

  conActivos(m: Matriz): boolean {
    return m.aplicaActivos === "SI";
  }
}
